/**
 * Pure investment compounding helpers for Wealth / Grow scenario explorer.
 * Assumed return % is persisted on the user profile (see AssumedReturnContext).
 */

/** Shared chart colors — principal (your money) vs interest (earned). */
export const PRINCIPAL_COLOR = '#38BDF8';
export const INTEREST_COLOR = '#3DD6A4';

export const DEFAULT_ANNUAL_RATE_PCT = 8;
export const MIN_ANNUAL_RATE_PCT = 1;
export const MAX_ANNUAL_RATE_PCT = 15;
export const DEFAULT_WEALTH_TARGET = 1_000_000;
export const MIN_WEALTH_TARGET = 50_000;
export const MAX_WEALTH_TARGET = 10_000_000;
export const DEFAULT_MILESTONE_STEP = 100_000;
export const MAX_PROJECTION_YEARS = 80;

export function clampWealthTarget(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_WEALTH_TARGET;
  return Math.max(MIN_WEALTH_TARGET, Math.min(MAX_WEALTH_TARGET, Math.round(value)));
}

export type CompoundYearRow = {
  year: number;
  yearIndex: number;
  startBalance: number;
  contributions: number;
  growth: number;
  endBalance: number;
  /** Money you put in: starting principal + cumulative contributions. */
  principalInvested: number;
  /** Cumulative compound growth (interest) — endBalance − principalInvested. */
  cumulativeGrowth: number;
};

export type CompoundSeriesPoint = { year: number; value: number };

export type WealthMilestone = {
  amount: number;
  yearIndex: number;
  calendarYear: number;
  /** Years from previous milestone (or from start for the first). */
  yearsFromPrevious: number;
  /** Years from start when this milestone is first hit. */
  cumulativeYears: number;
  /** Share of total time-to-target spent reaching this milestone. */
  timeSharePct: number;
};

export type WealthMilestoneInsight = {
  amount: number;
  timeSharePct: number;
};

export type WealthMilestonePlan = {
  target: number;
  step: number;
  reached: boolean;
  alreadyPastTarget: boolean;
  totalYears: number | null;
  milestones: WealthMilestone[];
  insights: WealthMilestoneInsight[];
  rows: CompoundYearRow[];
};

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

export function clampAnnualRatePct(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_ANNUAL_RATE_PCT;
  return Math.max(MIN_ANNUAL_RATE_PCT, Math.min(MAX_ANNUAL_RATE_PCT, value));
}

export function weightedPortfolioRatePct(
  holdings: { value: number; expectedReturnPct?: number }[],
): number {
  const total = holdings.reduce((sum, h) => sum + Math.max(0, h.value), 0);
  if (total <= 0) return DEFAULT_ANNUAL_RATE_PCT;
  const weighted = holdings.reduce(
    (sum, h) =>
      sum +
      (Math.max(0, h.value) / total) * (h.expectedReturnPct ?? DEFAULT_ANNUAL_RATE_PCT),
    0,
  );
  return clampAnnualRatePct(Math.round(weighted * 10) / 10);
}

export function formatWealthAmount(amount: number): string {
  if (amount >= 1_000_000) {
    const millions = amount / 1_000_000;
    return Number.isInteger(millions) ? `$${millions}M` : `$${millions.toFixed(1)}M`;
  }
  if (amount >= 1_000) return `$${Math.round(amount / 1_000)}k`;
  return `$${Math.round(amount)}`;
}

/** One decimal for segment durations (e.g. 1.2y) — avoids lumping 1.2 + 0.8 into “2y”. */
export function formatMilestoneYears(years: number): string {
  if (!Number.isFinite(years) || years <= 0) return '0';
  const rounded = Math.round(years * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/**
 * Interpolate inside the year the balance first crosses `amount`
 * so later $100k stretches can show 1.2y instead of a blunt 2y.
 */
function yearsWhenCrossing(rows: CompoundYearRow[], amount: number): number | null {
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i]!;
    const prev = rows[i - 1]!;
    if (row.endBalance < amount) continue;

    const from = prev.endBalance;
    const to = row.endBalance;
    if (from >= amount) {
      return prev.yearIndex;
    }
    if (to <= from) {
      return row.yearIndex;
    }
    const frac = Math.max(0, Math.min(1, (amount - from) / (to - from)));
    return roundYears((row.yearIndex - 1) + frac);
  }
  return null;
}

function roundYears(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * Project annual compounding with end-of-year contributions.
 * Tracks principal invested (your money) vs cumulative interest separately.
 */
export function projectCompounding(params: {
  principal: number;
  annualRatePct: number;
  annualContribution: number;
  years: number;
  startYear: number;
}): CompoundYearRow[] {
  const rate = Math.max(0, params.annualRatePct) / 100;
  const contrib = Math.max(0, params.annualContribution);
  let balance = Math.max(0, params.principal);
  let principalInvested = Math.max(0, params.principal);
  let cumulativeGrowth = 0;
  const rows: CompoundYearRow[] = [];

  for (let i = 0; i <= params.years; i++) {
    const year = params.startYear + i;
    if (i === 0) {
      rows.push({
        year,
        yearIndex: 0,
        startBalance: roundMoney(balance),
        contributions: 0,
        growth: 0,
        endBalance: roundMoney(balance),
        principalInvested: roundMoney(principalInvested),
        cumulativeGrowth: 0,
      });
      continue;
    }

    const startBalance = balance;
    const contributions = contrib;
    principalInvested += contributions;
    const afterContrib = startBalance + contributions;
    const growth = afterContrib * rate;
    const endBalance = afterContrib + growth;
    cumulativeGrowth += growth;
    balance = endBalance;

    rows.push({
      year,
      yearIndex: i,
      startBalance: roundMoney(startBalance),
      contributions: roundMoney(contributions),
      growth: roundMoney(growth),
      endBalance: roundMoney(endBalance),
      principalInvested: roundMoney(principalInvested),
      cumulativeGrowth: roundMoney(cumulativeGrowth),
    });
  }

  return rows;
}

export function rowsToSeriesPoints(rows: CompoundYearRow[]): CompoundSeriesPoint[] {
  return rows.map((r) => ({ year: r.year, value: r.endBalance }));
}

export function rowsToPrincipalPoints(rows: CompoundYearRow[]): CompoundSeriesPoint[] {
  return rows.map((r) => ({ year: r.year, value: r.principalInvested }));
}

export function rowsToInterestPoints(rows: CompoundYearRow[]): CompoundSeriesPoint[] {
  return rows.map((r) => ({ year: r.year, value: r.cumulativeGrowth }));
}

export type PrincipalInterestSplit = {
  id: string;
  label: string;
  ratePct: number;
  color: string;
  principal: number;
  interest: number;
  total: number;
  interestSharePct: number;
};

export function splitFromRows(
  id: string,
  label: string,
  ratePct: number,
  color: string,
  rows: CompoundYearRow[],
): PrincipalInterestSplit {
  const last = rows[rows.length - 1];
  const principal = last?.principalInvested ?? 0;
  const interest = last?.cumulativeGrowth ?? 0;
  const total = last?.endBalance ?? principal + interest;
  const interestSharePct = total > 0 ? Math.round((interest / total) * 100) : 0;
  return { id, label, ratePct, color, principal, interest, total, interestSharePct };
}

/**
 * Project until `target` (or max years) and mark every `step` dollar milestone
 * so the UI can show how long each $100k chunk takes.
 */
export function buildWealthMilestones(params: {
  principal: number;
  annualRatePct: number;
  annualContribution: number;
  startYear: number;
  target?: number;
  step?: number;
  maxYears?: number;
}): WealthMilestonePlan {
  const target = clampWealthTarget(params.target ?? DEFAULT_WEALTH_TARGET);
  // Keep ~5–10 milestones; shrink step for smaller custom targets.
  const defaultStep = params.step ?? DEFAULT_MILESTONE_STEP;
  const step = Math.max(
    5_000,
    Math.min(defaultStep, Math.round(target / 8 / 5_000) * 5_000 || 5_000),
  );
  const maxYears = Math.max(1, params.maxYears ?? MAX_PROJECTION_YEARS);
  const principal = Math.max(0, params.principal);

  const alreadyPastTarget = principal >= target;
  const rows = projectCompounding({
    principal,
    annualRatePct: params.annualRatePct,
    annualContribution: params.annualContribution,
    years: alreadyPastTarget ? 0 : maxYears,
    startYear: params.startYear,
  });

  if (alreadyPastTarget) {
    return {
      target,
      step,
      reached: true,
      alreadyPastTarget: true,
      totalYears: 0,
      milestones: [],
      insights: [],
      rows,
    };
  }

  // Trim rows once target is crossed so charts stay tight.
  const hitIndex = rows.findIndex((r) => r.endBalance >= target && r.yearIndex > 0);
  const trimmed =
    hitIndex >= 0 ? rows.slice(0, hitIndex + 1) : rows;

  const amounts: number[] = [];
  for (let amount = step; amount < target; amount += step) {
    amounts.push(amount);
  }
  amounts.push(target);

  const raw: Omit<WealthMilestone, 'timeSharePct'>[] = [];
  let prevYears = 0;
  for (const amount of amounts) {
    if (principal >= amount) continue;
    const cumulativeYears = yearsWhenCrossing(trimmed, amount);
    if (cumulativeYears == null) break;
    const yearIndex = Math.max(1, Math.ceil(cumulativeYears));
    const row = trimmed.find((r) => r.yearIndex === yearIndex) ?? trimmed[trimmed.length - 1]!;
    const yearsFromPrevious = roundYears(Math.max(0, cumulativeYears - prevYears));
    raw.push({
      amount,
      yearIndex,
      calendarYear: row.year,
      yearsFromPrevious,
      cumulativeYears,
    });
    prevYears = cumulativeYears;
  }

  const reached = raw.some((m) => m.amount >= target);
  const totalYears = reached
    ? (raw.find((m) => m.amount >= target)?.cumulativeYears ?? null)
    : null;

  const milestones: WealthMilestone[] = raw.map((m) => ({
    ...m,
    timeSharePct:
      totalYears && totalYears > 0
        ? Math.round((m.cumulativeYears / totalYears) * 100)
        : 0,
  }));

  const insights: WealthMilestoneInsight[] = [];
  const first = milestones[0];
  if (first) {
    insights.push({ amount: first.amount, timeSharePct: first.timeSharePct });
  }
  const mid = milestones.find((m) => m.timeSharePct >= 50 && m.amount < target);
  if (mid && (!first || mid.amount !== first.amount)) {
    insights.push({ amount: mid.amount, timeSharePct: mid.timeSharePct });
  }

  return {
    target,
    step,
    reached,
    alreadyPastTarget: false,
    totalYears,
    milestones,
    insights,
    rows: trimmed,
  };
}
